if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
        this.touchStartX = null;
        this.touchStartY = null;
        this.preloadCache = new Map();
        this.addEventListener('click', this.onModalClick.bind(this));
        this.addEventListener('keyup', this.onModalKeyup.bind(this));
        this.addEventListener('touchstart', this.onTouchStart.bind(this), { passive: true });
        this.addEventListener('touchend', this.onTouchEnd.bind(this), { passive: true });
      }

      hide() {
        super.hide();
      }

      show(opener) {
        if (window.matchMedia('(max-width: 749px)').matches) return;

        super.show(opener);
        this.showActiveMedia();
        this.preloadNeighbors();
      }

      onModalClick(event) {
        const thumb = event.target.closest('.product-media-modal__thumb');
        if (thumb) {
          this.revealMedia(thumb.getAttribute('data-media-id'));
          return;
        }

        if (event.target.closest('.product-media-modal__arrow--prev')) {
          this.stepActiveMedia(-1);
          return;
        }

        if (event.target.closest('.product-media-modal__arrow--next')) {
          this.stepActiveMedia(1);
        }
      }

      onModalKeyup(event) {
        if (!this.hasAttribute('open')) return;
        if (event.code === 'ArrowLeft') this.stepActiveMedia(-1);
        if (event.code === 'ArrowRight') this.stepActiveMedia(1);
      }

      onTouchStart(event) {
        if (!this.hasAttribute('open') || event.touches.length !== 1) return;
        if (!event.target.closest('.product-media-modal__stage')) return;
        if (event.target.closest('.product-media-modal__arrow')) return;

        const touch = event.touches[0];
        this.touchStartX = touch.clientX;
        this.touchStartY = touch.clientY;
      }

      onTouchEnd(event) {
        if (this.touchStartX == null || event.changedTouches.length !== 1) return;

        const touch = event.changedTouches[0];
        const deltaX = touch.clientX - this.touchStartX;
        const deltaY = touch.clientY - this.touchStartY;
        this.touchStartX = null;
        this.touchStartY = null;

        if (Math.abs(deltaX) < 40 || Math.abs(deltaX) < Math.abs(deltaY)) return;

        this.stepActiveMedia(deltaX < 0 ? 1 : -1);
      }

      get visibleThumbs() {
        return Array.from(this.querySelectorAll('.product-media-modal__thumb')).filter(
          (thumb) => getComputedStyle(thumb).display !== 'none'
        );
      }

      getViewerMedia(mediaId) {
        return this.querySelector(`.product-media-modal__viewer [data-media-id="${mediaId}"]`);
      }

      stepActiveMedia(direction) {
        const thumbs = this.visibleThumbs;
        if (thumbs.length < 2) return;

        const activeId = this.querySelector('.product-media-modal__viewer [data-media-id].active')?.getAttribute(
          'data-media-id'
        );
        const currentIndex = thumbs.findIndex((thumb) => thumb.getAttribute('data-media-id') === activeId);
        const nextIndex = (currentIndex + direction + thumbs.length) % thumbs.length;
        this.revealMedia(thumbs[nextIndex].getAttribute('data-media-id'));
      }

      showActiveMedia() {
        const mediaId = this.openedBy?.getAttribute('data-media-id');
        if (mediaId) this.setActiveMedia(mediaId);
      }

      async revealMedia(mediaId) {
        if (!mediaId) return;

        const viewer = this.querySelector('.product-media-modal__viewer');
        const nextMedia = this.getViewerMedia(mediaId);
        viewer?.setAttribute('aria-busy', 'true');

        await this.withTimeout(this.preloadViewerImage(nextMedia), 800);
        this.setActiveMedia(mediaId);
        viewer?.removeAttribute('aria-busy');
        this.preloadNeighbors();
      }

      setActiveMedia(mediaId) {
        if (!mediaId) return;

        this.querySelectorAll('.product-media-modal__viewer [data-media-id]').forEach((element) => {
          element.classList.toggle('active', element.getAttribute('data-media-id') === mediaId);
        });

        this.querySelectorAll('.product-media-modal__thumb').forEach((thumb) => {
          const isCurrent = thumb.getAttribute('data-media-id') === mediaId;
          thumb.classList.toggle('is-active', isCurrent);
          thumb.setAttribute('aria-current', isCurrent ? 'true' : 'false');
        });

        const activeMedia = this.getViewerMedia(mediaId);
        if (!activeMedia) return;

        const activeThumb = this.querySelector(`.product-media-modal__thumb[data-media-id="${mediaId}"]`);
        activeThumb?.scrollIntoView({ block: 'nearest', inline: 'nearest' });

        const activeMediaTemplate = activeMedia.querySelector('template');
        const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;

        if (activeMedia.nodeName == 'DEFERRED-MEDIA' && activeMediaContent) activeMedia.loadContent();
      }

      preloadNeighbors() {
        const thumbs = this.visibleThumbs;
        if (thumbs.length < 2) return;

        const activeId = this.querySelector('.product-media-modal__viewer [data-media-id].active')?.getAttribute(
          'data-media-id'
        );
        const currentIndex = thumbs.findIndex((thumb) => thumb.getAttribute('data-media-id') === activeId);
        if (currentIndex < 0) return;

        const neighborIndexes = [
          (currentIndex - 1 + thumbs.length) % thumbs.length,
          (currentIndex + 1) % thumbs.length,
        ];

        neighborIndexes.forEach((index) => {
          const mediaId = thumbs[index].getAttribute('data-media-id');
          this.preloadViewerImage(this.getViewerMedia(mediaId));
        });
      }

      preloadViewerImage(mediaEl) {
        if (!mediaEl) return Promise.resolve();

        const img = mediaEl.nodeName === 'IMG' ? mediaEl : mediaEl.querySelector('img');
        if (!img) return Promise.resolve();

        img.loading = 'eager';
        img.decoding = 'async';

        const srcset = img.getAttribute('srcset') || '';
        const src = img.getAttribute('src') || '';
        const sizes = img.getAttribute('sizes') || '';
        const cacheKey = `${srcset}|${src}|${sizes}`;

        if (this.preloadCache.has(cacheKey)) return this.preloadCache.get(cacheKey);

        const promise = new Promise((resolve) => {
          const finish = () => resolve();
          const preloader = new Image();
          preloader.decoding = 'async';
          if (sizes) preloader.sizes = sizes;
          if (srcset) preloader.srcset = srcset;
          preloader.src = src;

          const settle = () => {
            if (typeof img.decode === 'function') {
              img.decode().then(finish).catch(finish);
            } else {
              finish();
            }
          };

          if (preloader.complete && preloader.naturalWidth > 0) {
            settle();
            return;
          }

          preloader.addEventListener('load', settle, { once: true });
          preloader.addEventListener('error', finish, { once: true });
        });

        this.preloadCache.set(cacheKey, promise);
        return promise;
      }

      withTimeout(promise, ms) {
        return Promise.race([promise, new Promise((resolve) => setTimeout(resolve, ms))]);
      }
    }
  );
}
